/**
 * The listing drawer's content (ListingDetailBody: carousel, facts,
 * residence block, map, seller sections, the tabs under them) is its own chunk; the frame
 * (ListingDetailPanel) stays with the page. Phones were downloading and
 * parsing ~35 KB of it on the list, map and landing pages before anyone
 * opened a listing (owner, 2026-10-19). It arrives
 *   - on intent: a pointer entering, a focus on or a finger touching a
 *     listing card (preloadDetailBody, ListingBrowseCard);
 *   - in idle time once the page has hydrated, wherever a drawer is
 *     mounted (unless the browser asks to save data): the first idle
 *     moment after the drawer mounted, so that a quick tap rarely finds
 *     it missing while the page's photos still fill a slow line;
 *   - together with the frame's own chunk where a page loads the frame
 *     only on the first click (withDetailBody);
 *   - at the latest with the first open, while the frame and its
 *     skeleton are already on screen.
 * It is fetched at high priority: on a slow line the photos the page is
 * still loading would otherwise share the bandwidth with it for seconds
 * (throttled probe, 2026-10-19). Once here, the drawer renders exactly
 * as it did when it was one file.
 */
import { useEffect, useState } from "react";
import { whenIdle } from "@/lib/idle";

/** What the frame renders from the chunk. */
const importBody = () => import(/* webpackFetchPriority: "high" */ "./ListingDetailBody").then(
  ({ DetailContent, DupGroupSection, OwnContactSection, SellerSection, ListingActionsMenu, ListingAroundSection }) =>
    ({ DetailContent, DupGroupSection, OwnContactSection, SellerSection, ListingActionsMenu, ListingAroundSection }),
);
type DetailBody = Awaited<ReturnType<typeof importBody>>;

let loaded: DetailBody | null = null;
let pending: Promise<DetailBody> | null = null;
/** Drawers mounted on the page; card intent only preloads where one can open. */
let frames = 0;

/** The idle download waits for an idle moment after the drawer mounted
 *  (past hydration's own work), at most this long. */
const IDLE_TIMEOUT_MS = 1000;

function loadDetailBody(): Promise<DetailBody> {
  if (loaded) return Promise.resolve(loaded);
  if (!pending) {
    pending = importBody().then(
      (m) => (loaded = m),
      (e: unknown) => {
        // A failed chunk may load on the next try (flaky network).
        pending = null;
        throw e;
      },
    );
  }
  return pending;
}

/** Card intent: start the download where a drawer can open. */
export function preloadDetailBody(): void {
  if (!loaded && frames > 0) loadDetailBody().catch(() => {});
}

/** For a page that fetches the drawer frame itself only on the first
 *  click (residence page, residences map, result page sections): the
 *  content chunk is requested together with the frame's, so the first
 *  open waits for one download, not two in a row. Frame first, content
 *  after put the content up to a few hundred ms behind the single chunk
 *  these pages had before (throttled residence page at 1280,
 *  2026-10-19); together they match it. Wraps the frame's import()
 *  inside the dynamic() call:
 *  `dynamic(() => withDetailBody(import("...ListingDetailPanel")))`. */
export function withDetailBody<T>(frame: Promise<T>): Promise<T> {
  if (!loaded) loadDetailBody().catch(() => {});
  return frame;
}

function saveData(): boolean {
  const c = (navigator as Navigator & { connection?: { saveData?: boolean } }).connection;
  return !!c?.saveData;
}

/** The content module for a mounted drawer frame, null until it has
 *  arrived. Mounting schedules the idle download; opening starts it at
 *  once. `failed` + `retry` cover a chunk that could not be fetched. */
export function useDetailBody(open: boolean): { body: DetailBody | null; failed: boolean; retry: () => void } {
  const [body, setBody] = useState<DetailBody | null>(loaded);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    frames += 1;
    let alive = true;
    const cancel = loaded || saveData()
      ? () => {}
      : whenIdle(() => {
          loadDetailBody().then((m) => { if (alive) setBody(m); }, () => {});
        }, IDLE_TIMEOUT_MS);
    return () => {
      frames -= 1;
      alive = false;
      cancel();
    };
  }, []);
  useEffect(() => {
    if (body || loaded || !open) return;
    let alive = true;
    loadDetailBody().then(
      (m) => { if (alive) { setFailed(false); setBody(m); } },
      () => { if (alive) setFailed(true); },
    );
    return () => { alive = false; };
  }, [open, body, attempt]);
  return {
    body: body ?? loaded,
    failed: failed && !(body ?? loaded),
    retry: () => { setFailed(false); setAttempt((n) => n + 1); },
  };
}
